// src/prototype/components/execute/ExecVendorLandscapeTab.tsx
// ============================================================
// Execute hub · Vendor Landscape tab (E7, Oct 21)
//
// Vendor categories with every sourced vendor (competitors included), the delivery path and
// RFP questions. Every named vendor carries its sources and retrieval date.
// The landscape is evidence only and never changes a score.
// ============================================================
import React from 'react';
import { Store, HelpCircle, Route as RouteIcon, Info, ExternalLink } from 'lucide-react';
import { SectionCard } from '../SectionCard';
import { getVendorLandscape, visibleVendors, DELIVERY_PATH_LABEL } from '../../data/execute/vendorLandscape';
import { MATURITY_MODELS } from '../../data/himss/himssAssessments';
import { useHimssAssessments } from '../../hooks/useHimssAssessments';
import useDemoUploads from '../../hooks/useDemoUploads';
import { ExecVendorEvaluation } from './ExecVendorEvaluation';
import { VENDOR_EVALUATIONS } from '../../data/execute/vendorEvaluationData';

export const ExecVendorLandscapeTab: React.FC<{ useCaseId: string; clientName: string; useCaseTitle?: string }> = ({ useCaseId, clientName, useCaseTitle = '' }) => {
  const evaluation = VENDOR_EVALUATIONS[useCaseId];
  const landscape = getVendorLandscape(useCaseId);
  const vendors = visibleVendors(landscape);
  const fmt = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const { himss } = useHimssAssessments();
  const { documents } = useDemoUploads();

  // Client context the fit notes are read against: D3 inventory and recorded maturity results.
  const d3 = documents.some((d) => /D3|platform|inventory/i.test(d.name));
  const heldModels = himss.noneHeld ? [] : MATURITY_MODELS.filter((m) => himss.models[m.id].held === true)
    .map((m) => `${m.name} Stage ${himss.models[m.id].stage ?? '?'}`);
  const asOf = new Date(`${landscape.asOf}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-4">
      {evaluation && (
        <>
          <ExecVendorEvaluation evaluation={evaluation} clientName={clientName} useCaseTitle={useCaseTitle} />
          <h2 className="text-[10px] font-bold tracking-widest text-[#717182] pt-4">FULL MARKET LANDSCAPE: THE EVIDENCE BEHIND THE EVALUATION</h2>
        </>
      )}
      <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3 flex items-start gap-3">
        <Info className="w-4 h-4 text-[#18365E] mt-0.5 shrink-0" />
        <div className="text-xs text-[#4b5563] leading-relaxed flex-1">
          <p>
            Every sourced vendor that can deliver this for {clientName}, by category. Evidence only; it does not change any score.
          </p>
          <p className="text-[11px] text-[#717182] mt-1">
            As of {asOf} · {landscape.generation}{landscape.reviewed ? '' : ' (general landscape for this kind of use case)'} · listed in no particular order
          </p>
        </div>
      </div>

      <SectionCard icon={<Info className="w-3.5 h-3.5" />} title="Your context used for fit">
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className={`px-2 py-1 rounded-full border ${d3 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-[#e5e7eb] text-[#717182]'}`}>
            {d3 ? 'Platform inventory (D3) on file' : 'Platform inventory (D3) not yet uploaded'}
          </span>
          {himss.noneHeld ? (
            <span className="px-2 py-1 rounded-full border border-[#e5e7eb] text-[#717182]">No HIMSS assessments held</span>
          ) : heldModels.length > 0 ? (
            heldModels.map((m) => <span key={m} className="px-2 py-1 rounded-full border border-[#e5e7eb] bg-[#F5F7FA] text-[#18365E]">{m} (client-reported)</span>)
          ) : (
            <span className="px-2 py-1 rounded-full border border-[#e5e7eb] text-[#717182]">HIMSS assessments not recorded</span>
          )}
        </div>
      </SectionCard>

      {/* Category index: what the market looks like at a glance, with vendor counts. */}
      <SectionCard icon={<Store className="w-3.5 h-3.5" />} title={`Vendor landscape · ${vendors.length} vendors in ${landscape.categories.length} categories`} accent>
        <div className="flex flex-wrap gap-2">
          {landscape.categories.map((c, i) => {
            const n = vendors.filter((v) => v.category === c.name).length;
            return (
              <a key={c.name} href={`#vl-cat-${i}`} className="text-[11px] font-medium text-[#18365E] border border-[#e5e7eb] rounded-full px-2.5 py-1 hover:border-[#18365E] bg-white">
                {c.name} <span className="text-[#717182] tabular-nums">{n}</span>
              </a>
            );
          })}
        </div>
        <p className="text-[11px] text-[#717182] mt-3">
          {vendors.length > 0
            ? 'Every vendor has a cited source. Time to deploy and cost appear only where a source states them. Outcome figures are vendor-reported unless the source is independent.'
            : 'Named vendors are added once each claim has a cited source. Time to deploy and cost will show only where a source states them.'}
        </p>
      </SectionCard>

      {landscape.categories.map((c, i) => {
        const inCat = vendors.filter((v) => v.category === c.name);
        return (
          <section key={c.name} id={`vl-cat-${i}`} className="rounded-xl border border-[#e5e7eb] bg-white p-4 scroll-mt-40">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-[#18365E]">{c.name}</h3>
              <span className="text-[11px] text-[#717182]">{inCat.length} vendor{inCat.length === 1 ? '' : 's'}</span>
              <span className="flex flex-wrap gap-1">
                {c.deploymentModels.map((d) => <span key={d} className="text-[10px] font-semibold text-[#717182] border border-[#e5e7eb] rounded px-1.5 py-0.5">{d}</span>)}
              </span>
            </div>
            <p className="text-xs text-[#4b5563] mt-1 leading-relaxed max-w-4xl">{c.covers}</p>
            <p className="text-[11px] text-[#717182] mt-1 max-w-4xl"><b className="text-[#18365E]">What decides fit:</b> {c.fitConsiderations}</p>
            {inCat.length > 0 && (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
                {inCat.map((v) => (
                  <article key={v.name} className="rounded-lg border border-[#e5e7eb] bg-[#FAFBFC] p-3 text-xs flex flex-col">
                    <div className="flex items-start gap-1.5 flex-wrap">
                      <h4 className="font-bold text-[#18365E] text-[13px] leading-snug">{v.name}</h4>
                      <span className="text-[10px] font-semibold text-[#717182] border border-[#e5e7eb] bg-white rounded px-1">{v.deploymentModel}</span>
                    </div>
                    {v.parentCompany && <p className="text-[10px] text-[#717182] mt-0.5">{v.parentCompany}</p>}
                    <p className="text-[#4b5563] mt-1.5 leading-relaxed">{v.capabilitySummary}</p>
                    {v.integrationNotes && <p className="text-[11px] text-[#717182] mt-1.5"><b className="text-[#18365E]">Integration:</b> {v.integrationNotes}</p>}
                    {v.evidence && v.evidence.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {v.evidence.slice(0, 3).map((e) => (
                          <li key={e} className="text-[11px] text-[#717182] flex gap-1"><span aria-hidden="true">·</span><span>{e}</span></li>
                        ))}
                      </ul>
                    )}
                    {(v.typicalTimeToDeploy || v.typicalCost) && (
                      <p className="text-[11px] text-[#717182] mt-1.5">
                        {v.typicalTimeToDeploy && <>Time to deploy: {v.typicalTimeToDeploy}. </>}
                        {v.typicalCost && <>Cost: {v.typicalCost}.</>}
                      </p>
                    )}
                    <details className="mt-auto pt-2 text-[10px] text-[#717182]">
                      <summary className="cursor-pointer hover:text-[#18365E]">
                        {v.sources.length} source{v.sources.length === 1 ? '' : 's'} · retrieved {fmt(v.sources[0].retrieved)}
                      </summary>
                      <ul className="mt-1 space-y-0.5">
                        {v.sources.map((src) => (
                          <li key={src.url}>
                            <a href={src.url} target="_blank" rel="noreferrer" className="inline-flex items-start gap-0.5 hover:text-[#FF6E4C] underline decoration-dotted">
                              {src.title}<ExternalLink className="w-2.5 h-2.5 mt-0.5 shrink-0" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {landscape.excluded && landscape.excluded.length > 0 && (
        <details className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
          <summary className="text-xs font-semibold text-[#18365E] cursor-pointer">
            Considered and not listed ({landscape.excluded.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {landscape.excluded.map((x) => (
              <li key={x.name} className="text-xs text-[#4b5563]"><b className="text-[#18365E]">{x.name}:</b> {x.reason}</li>
            ))}
          </ul>
        </details>
      )}

      <div className="grid md:grid-cols-2 gap-4 items-start">
        <SectionCard icon={<RouteIcon className="w-3.5 h-3.5" />} title="Build, buy or partner">
          <p className="text-xs font-bold text-[#18365E]">{DELIVERY_PATH_LABEL[landscape.deliveryPath.lean]}</p>
          <p className="text-xs text-[#4b5563] mt-1 leading-relaxed">{landscape.deliveryPath.note}</p>
        </SectionCard>
        <SectionCard icon={<HelpCircle className="w-3.5 h-3.5" />} title="Questions for your RFP">
          <ol className="list-decimal ml-4 space-y-1.5 text-xs text-[#4b5563]">
            {landscape.rfpQuestions.map((q) => <li key={q}>{q}</li>)}
          </ol>
        </SectionCard>
      </div>
    </div>
  );
};

export default ExecVendorLandscapeTab;
